import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
} from '@angular/fire/firestore';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, switchMap, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { Auth } from '@angular/fire/auth';
import { UserPreferencesService } from './user-preferences.service';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  constructor(
    private firestore: Firestore,
    private http: HttpClient,
    private auth: Auth,
    private userPreferences: UserPreferencesService,
  ) {}

  // Get a single document as an observable (like docData)
  getDocument<T>(
    collectionName: string,
    docId: string,
  ): Observable<T | undefined> {
    const docRef = doc(this.firestore, collectionName, docId);
    return docData(docRef) as Observable<T | undefined>;
  }

  // Get all documents from a collection
  getCollection<T>(
    collectionName: string,
    applyYearFilter: boolean = true,
  ): Observable<T[]> {
    const collectionRef = collection(this.firestore, collectionName);

    // For progress-data, use getDocs to fetch all data at once
    if (collectionName === 'progress-data' && !applyYearFilter) {
      const q = query(collectionRef, orderBy('date', 'asc'));
      return from(getDocs(q)).pipe(
        map((snapshot) => snapshot.docs.map((doc) => doc.data() as T)),
      );
    }

    const data$ = collectionData(collectionRef) as Observable<T[]>;

    if (!applyYearFilter) {
      return data$;
    }

    // Apply year filtering for portal-data and QuickBooks collections
    const collectionsToFilter = [
      'portal-data',
      'from-quickbooks',
      'from-quickbooks-raw',
    ];

    if (collectionsToFilter.includes(collectionName)) {
      return data$.pipe(
        switchMap(async (items) => {
          return await this.filterByEnabledYears(items, collectionName);
        }),
      );
    }

    return data$;
  }

  // Get filtered documents from a collection
  getFilteredCollection<T>(
    collectionName: string,
    fieldPath: string,
    operator: any,
    value: any,
  ): Observable<T[]> {
    const collectionRef = collection(this.firestore, collectionName);
    const q = query(collectionRef, where(fieldPath, operator, value));
    return collectionData(q) as Observable<T[]>;
  }

  // Get progress data filtered by date range (optimized for performance)
  getProgressDataByDateRange<T>(
    startDate: string,
    endDate?: string,
  ): Observable<T[]> {
    const collectionRef = collection(this.firestore, 'progress-data');
    let q;

    if (endDate) {
      // Query for date range
      q = query(
        collectionRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc'),
      );
    } else {
      // Query for dates >= startDate
      q = query(
        collectionRef,
        where('date', '>=', startDate),
        orderBy('date', 'asc'),
      );
    }

    return collectionData(q) as Observable<T[]>;
  }

  // Add a new document to a collection
  async addDocument(
    collectionName: string,
    data: any,
    id?: string,
  ): Promise<void> {
    const collectionRef = collection(this.firestore, collectionName);
    if (id) {
      await setDoc(doc(collectionRef, id), data);
    } else {
      await setDoc(doc(collectionRef), data);
    }
  }

  // Update an existing document
  async updateDocument(
    collectionName: string,
    id: string,
    data: any,
  ): Promise<void> {
    const docRef = doc(this.firestore, collectionName, id);
    await updateDoc(docRef, data);
  }

  // Delete a document
  async deleteDocument(collectionName: string, id: string): Promise<void> {
    const docRef = doc(this.firestore, collectionName, id);
    await deleteDoc(docRef);
  }

  /**
   * Fetch purchase orders from the correct Firestore database (sheets-data) via backend HTTP endpoint
   */
  getPurchaseOrders(): Observable<any[]> {
    // Get the current user's ID token and include it in the request
    return from(
      this.auth.currentUser?.getIdToken() || Promise.resolve(null),
    ).pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        });

        return this.http.get<any[]>(
          `${environment.functionsUrl}/purchaseOrders`,
          { headers },
        );
      }),
    );
  }

  /**
   * Filter data by enabled years based on user preferences
   */
  private async filterByEnabledYears(
    data: any[],
    collectionName?: string,
  ): Promise<any[]> {
    try {
      const enabledYears = await this.userPreferences.getEnabledYears();
      console.log(
        `[DataService] Filtering ${
          collectionName || 'collection'
        } by enabled years:`,
        enabledYears,
      );

      if (!enabledYears || enabledYears.length === 0) {
        return data;
      }

      const filtered = data.filter((item) => {
        // For QuickBooks collections, use date fields
        if (
          collectionName === 'from-quickbooks' ||
          collectionName === 'from-quickbooks-raw'
        ) {
          // from-quickbooks (expenses/line items) use 'date' field (extracted from bill.TxnDate)
          if (item.date) {
            const year = this.extractYear(item.date);
            if (year) {
              return enabledYears.includes(year);
            }
          }
          // from-quickbooks-raw (bills) use TxnDate as primary date
          if (item.TxnDate) {
            const year = this.extractYear(item.TxnDate);
            if (year) {
              return enabledYears.includes(year);
            }
          }
          // Fallback to MetaData.CreateTime
          if (item.MetaData?.CreateTime) {
            const year = this.extractYear(item.MetaData.CreateTime);
            if (year) {
              return enabledYears.includes(year);
            }
          }
          // If no date found, exclude
          return false;
        }

        // For portal-data, prioritize creation date fields
        const creationDateFields = [
          'date_created',
          'dateCreated',
          'createdDate',
        ];

        // First check creation date
        for (const field of creationDateFields) {
          if (item[field]) {
            const year = this.extractYear(item[field]);
            if (year) {
              // If we found a creation year, use it to determine inclusion
              return enabledYears.includes(year);
            }
          }
        }

        // If no creation date found, check other date fields as fallback
        const otherDateFields = [
          'date_updated',
          'dateUpdated',
          'start_date',
          'startDate',
          'due_date',
          'dueDate',
          'date_closed',
          'dateClosed',
        ];

        for (const field of otherDateFields) {
          if (item[field]) {
            const year = this.extractYear(item[field]);
            if (year) {
              return enabledYears.includes(year);
            }
          }
        }

        // If no date fields found at all, exclude the item
        console.warn('[DataService] Item has no date fields:', item);
        return false;
      });

      console.log(
        `[DataService] Filtered ${collectionName || 'collection'}: ${
          data.length
        } items to ${filtered.length} items`,
      );
      return filtered;
    } catch (error) {
      console.error('[DataService] Error filtering by years:', error);
      return data;
    }
  }

  /**
   * Extract year from various date formats
   */
  private extractYear(date: any): number | null {
    try {
      let dateObj: Date;

      if (date && date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } else if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'string' || typeof date === 'number') {
        dateObj = new Date(date);
      } else {
        return null;
      }

      if (isNaN(dateObj.getTime())) {
        return null;
      }

      return dateObj.getFullYear();
    } catch (error) {
      return null;
    }
  }
}
